import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { FormComponent, UiComponent } from 'components';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { usePageOption } from 'hooks';
import { StickerQuery, UserQuery } from 'queries';
import * as M from 'styles/mobile';
import { IStickerListItem, IUserListItem, SearchUserFilterType } from 'types';
import * as S from './style';

const getPhoneLast4 = (phone?: string) =>
  (phone ?? '').replace(/\D/g, '').slice(-4);

interface Props {
  sticker: IStickerListItem;
}

function UserGrant({ sticker }: Props) {
  const [filter, setFilter] = useState<SearchUserFilterType>({});
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [grantedIds, setGrantedIds] = useState<number[]>([]);

  const { mutateAsync: grant } = StickerQuery.useGrant();
  const { pageOption, handlePage } = usePageOption(10);
  const { data, isLoading } = UserQuery.useGetAll({
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const users: IUserListItem[] = data?.data ?? [];

  const handleSearch = (d: SearchUserFilterType) => {
    setFilter({ name: d.name || undefined });
    handlePage({ ...pageOption, page: 1 });
  };

  const handleGrant = async (userId: number, name: string) => {
    if (pendingId) return;

    setPendingId(userId);
    try {
      const res = await grant({ id: sticker.id, userIds: [userId] });
      toast.success(
        res.data.skipped
          ? `${name}님은 이미 보유 중입니다`
          : `${name}님에게 지급했습니다`,
      );
      setGrantedIds((prev) => [...prev, userId]);
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <>
      <FormComponent.Form submit={handleSearch}>
        <FormComponent.SearchInput
          name="name"
          placeholder="이름으로 검색해주세요"
        />
      </FormComponent.Form>
      <div css={S.sheetList}>
        {users.map(({ id, name, phone }) => {
          const last4 = getPhoneLast4(phone);
          const isGranted = grantedIds.includes(id);
          return (
            <div key={id} css={S.row}>
              <p css={[S.info, S.name]}>
                {name}
                {last4 && <span>({last4})</span>}
              </p>
              <UiComponent.Button
                variant={isGranted ? 'border' : 'contained'}
                color="primary"
                css={S.action}
                disabled={isGranted || pendingId === id}
                onClick={() => handleGrant(id, name)}
              >
                {isGranted ? '지급됨' : '지급'}
              </UiComponent.Button>
            </div>
          );
        })}
        {!isLoading && users.length === 0 && (
          <p css={M.empty}>검색 결과가 없습니다</p>
        )}
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </>
  );
}

export default UserGrant;
